(() => {
  'use strict';
  const ROOT = window.GeightConfig?.root || window.Shopify?.routes?.root || '/';
  const MOQ = 50;
  let cartBusy = false;
  function integer(value, label = 'Quantity') {
    const n = Number(value);
    if (!Number.isSafeInteger(n) || n < 1 || n > 99999) throw new Error(`${label} must be a whole number from 1 to 99999.`);
    return n;
  }
  async function json(url, options = {}) {
    const response = await fetch(url, {credentials:'same-origin', cache:'no-store', ...options, signal: options.signal || AbortSignal.timeout(30000)});
    let data;
    try { data = await response.json(); } catch (_) { throw new Error('The store returned an unexpected response. Please refresh and try again.'); }
    if (!response.ok) throw new Error(data.description || data.message || data.error?.message || 'The store could not complete the request.');
    return data;
  }
  const cartQty = (cart, id) => cart.items.filter(x => String(x.variant_id || x.id) === String(id)).reduce((n,x) => n + x.quantity, 0);
  async function variantData(handleOrUrl, id) {
    if (!/^\d+$/.test(String(id))) throw new Error('Please select an available product option.');
    if (!handleOrUrl) throw new Error('Product details are unavailable. Please reopen this product.');
    const url = new URL(String(handleOrUrl).includes('/') ? handleOrUrl : ROOT + 'products/' + encodeURIComponent(handleOrUrl), location.origin);
    if (url.origin !== location.origin || !url.pathname.includes('/products/')) throw new Error('Invalid product link.');
    url.searchParams.set('variant', String(id)); url.searchParams.set('view','geight-quick-order-data'); url.searchParams.set('_geight',Date.now());
    const data = await json(url);
    if (data.quick_order_version !== 2 || String(data.variant_id) !== String(id)) throw new Error('Product availability could not be checked. Please refresh this page.');
    return data;
  }
  function validate(data, qty, existing = 0) {
    integer(qty);
    if (!data.available) throw new Error('This option is sold out.');
    const r = data.quantity_rule;
    if (!r || !Number.isInteger(r.min) || r.min < 1 || !Number.isInteger(r.increment) || r.increment < 1 || (r.max !== null && (!Number.isInteger(r.max) || r.max < 1))) throw new Error('Product quantity rules are unavailable.');
    const min = Math.max(r.min, data.preorder ? MOQ : 1);
    if (qty < min) throw new Error(`${data.preorder ? 'Preorders' : 'This option'} require at least ${min} units. Please update the quantity.`);
    if (qty % r.increment !== 0) throw new Error(`Use quantities in multiples of ${r.increment}.`);
    if (r.max !== null && existing + qty > r.max) throw new Error(`Maximum ${r.max} units per option; ${existing} already in your cart.`);
    if (data.tracked && !data.preorder) {
      if (!Number.isInteger(data.inventory_quantity)) throw new Error('Inventory could not be checked.');
      if (existing + qty > data.inventory_quantity) throw new Error(`Only ${Math.max(0,data.inventory_quantity - existing)} more units available.`);
    }
    return qty;
  }
  async function validateForm(form, url) {
    if (!form) throw new Error('Product form not found.');
    const id = form.querySelector('[name=id]')?.value;
    const qty = integer(form.querySelector('[name=quantity]')?.value || 1);
    const [data, cart] = await Promise.all([variantData(url,id), json(ROOT+'cart.js')]);
    if (form.querySelector('[name=id]')?.value !== id || integer(form.querySelector('[name=quantity]')?.value || 1) !== qty) throw new Error('Your option or quantity changed. Please add it again.');
    validate(data,qty,cartQty(cart,id));
    let prop = form.querySelector('[name="properties[pre_order_item]"]');
    if (data.preorder) {
      if (!prop) { prop=document.createElement('input');prop.type='hidden';prop.name='properties[pre_order_item]';form.append(prop); }
      prop.value='Yes';
    } else prop?.remove();
    const status=form.querySelector('[data-geight-product-status]'); if(status) status.textContent='';
    return data;
  }
  async function addItems(rawItems) {
    if(cartBusy) throw new Error('Please wait for the current cart update to finish.');
    cartBusy=true;
    let submitted=false;
    try {
      const groups=new Map();
      for(const item of rawItems){
        const id=String(item.variant_id || item.id || ''); const qty=integer(item.quantity);
        if(!/^\d+$/.test(id)) throw new Error('A saved option is no longer available. Remove it from your selection.');
        const group=groups.get(id);
        if(group) group.quantity=integer(group.quantity+qty);
        else groups.set(id,{id,quantity:qty,handle:item.product_handle || item.handle});
      }
      if(!groups.size) throw new Error('Select at least one item.');
      const before=await json(ROOT+'cart.js');const ready=[]; const failedItems=[];
      // Bound requests while checking all items before any write.
      const items=[...groups.values()];let cursor=0;
      await Promise.all(Array.from({length:Math.min(4,items.length)},async()=>{
        while(cursor<items.length){const g=items[cursor++];try{
          const data=await variantData(g.handle,g.id);
          if(data.requires_selling_plan || data.requires_components || data.gift_card) throw new Error('Open this product to choose its purchase options.');
          validate(data,g.quantity,cartQty(before,g.id));
          ready.push({id:g.id,quantity:g.quantity,...(data.preorder ? {properties:{pre_order_item:'Yes'}} : {})});
        }catch(e){failedItems.push({id:g.id,message:e.message});}}
      }));
      if(!ready.length) throw new Error(failedItems[0]?.message || 'No available items.');
      // One request per batch; never automatically retry a write with an uncertain result.
      for(let start=0;start<ready.length;start+=20){
        submitted=true;
        try { await json(ROOT+'cart/add.js',{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({items:ready.slice(start,start+20)})}); }
        catch(e){throw new Error(`Cart update stopped: ${e.message} Open your cart and check quantities before trying again; some items may already have been added.`);}
      }
      const after=await json(ROOT+'cart.js');
      if(ready.some(x=>cartQty(after,x.id)-cartQty(before,x.id)!==x.quantity)) throw new Error('Cart quantities could not be confirmed. Open your cart before adding again.');
      if(failedItems.length){
        // Expose concrete validation reasons, including preorder MOQ, instead of treating everything as sold out.
        window.dispatchEvent(new CustomEvent('geight:cart-notice',{detail:failedItems.map(x=>x.message).slice(0,3).join(' ')}));
      }
      return {addedLines:ready.length,addedUnits:ready.reduce((n,x)=>n+x.quantity,0),failedItems};
    } finally {
      cartBusy=false;
      if(submitted) window.dispatchEvent(new Event('geight:cart-changed'));
    }
  }
  function syncCard(form){
    const option=form.querySelector('[name=id]')?.selectedOptions?.[0];const input=form.querySelector('[name=quantity]');if(!option||!input)return;
    input.min=option.dataset.min || 1;input.step=option.dataset.step || 1;
    if(option.dataset.max)input.max=option.dataset.max;else input.removeAttribute('max');
    if(form.dataset.variant!==option.value){input.value=input.min;form.dataset.variant=option.value;}
    const stock=form.querySelector('[data-geight-stock]');
    if(stock)stock.textContent=option.disabled?'Sold out':option.dataset.preorder==='true'?'Preorder · minimum 50 units':option.dataset.tracked==='true'?`${Math.max(0,Number(option.dataset.stock))} available`:'Available';
  }
  document.addEventListener('change',e=>{const form=e.target.closest('[data-geight-card-form]');if(form&&e.target.name==='id')syncCard(form);});
  document.addEventListener('submit',async e=>{
    const form=e.target.closest('[data-geight-card-form]');if(!form)return;
    e.preventDefault();e.stopImmediatePropagation();if(form.dataset.busy==='true')return;
    const button=form.querySelector('[type=submit]'),status=form.querySelector('[data-geight-status]');
    form.dataset.busy='true';button.disabled=true;status.dataset.error='false';status.textContent='Adding…';
    try{await addItems([{variant_id:form.querySelector('[name=id]').value,quantity:form.querySelector('[name=quantity]').value,product_handle:form.dataset.productHandle}]);status.textContent='Added to cart.';}
    catch(error){status.dataset.error='true';status.textContent=error.message;}
    finally{button.disabled=false;form.dataset.busy='false';}
  },true);
  document.addEventListener('click',async e=>{
    const btn=e.target.closest('[data-geight-subscribe]');if(!btn)return;
    e.preventDefault();const box=btn.closest('[data-geight-restock]'),input=box.querySelector('[data-geight-email]'),status=box.querySelector('[data-geight-status]');
    if(!input.value.trim()||!input.checkValidity()){input.reportValidity();status.textContent='Please enter a valid email address.';return;}
    if(btn.disabled)return;btn.disabled=true;status.textContent='Submitting…';
    try{const result=await json(ROOT+'apps/wishlist/back-in-stock/subscribe',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({email:input.value.trim(),variant_id:box.dataset.variantId})});
      if(result.ok!==true)throw new Error(result.error?.message||'Could not save your request.');status.textContent='You are on the waitlist. We will email you when this option is back.';
    }catch(error){status.textContent=error.message;}finally{btn.disabled=false;}
  });
  function scan(root){if(root.matches?.('[data-geight-card-form]'))syncCard(root);root.querySelectorAll?.('[data-geight-card-form]').forEach(syncCard);}
  function start(){scan(document);new MutationObserver(records=>records.forEach(r=>r.addedNodes.forEach(n=>{if(n.nodeType===1)scan(n);}))).observe(document.body,{childList:true,subtree:true});}
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',start);else start();
  let checkoutBusy=false;
  const approvedForms=new WeakSet();
  async function checkCartPreorders(){
    const cart=await json(ROOT+'cart.js');
    const groups=new Map();
    for(const line of cart.items){
      const id=String(line.variant_id);const old=groups.get(id);
      if(old)old.quantity+=line.quantity;else groups.set(id,{id,quantity:line.quantity,handle:line.handle || line.url});
    }
    const all=[...groups.values()];let next=0;
    await Promise.all(Array.from({length:Math.min(4,all.length)},async()=>{
      while(next<all.length){const row=all[next++];const data=await variantData(row.handle,row.id);
        if(data.preorder && row.quantity<MOQ)throw new Error(`A preorder option has ${row.quantity} units. Increase it to at least 50 units or remove it before checkout.`);
      }
    }));
  }
  function checkoutError(message,anchor){
    let node=document.querySelector('[data-geight-checkout-error]');
    if(!node){node=document.createElement('p');node.dataset.geightCheckoutError='';node.className='geight-checkout-error';node.setAttribute('role','alert');(anchor?.closest('form')||document.querySelector('main')||document.body).append(node);}
    node.textContent=message;node.scrollIntoView({block:'nearest'});
  }
  document.addEventListener('submit',async event=>{
    const form=event.target;
    if(!event.submitter || event.submitter.name!=='checkout')return;
    if(approvedForms.has(form)){approvedForms.delete(form);return;}
    event.preventDefault();event.stopImmediatePropagation();if(checkoutBusy)return;
    checkoutBusy=true;const button=event.submitter;button.disabled=true;
    try{await checkCartPreorders();button.disabled=false;approvedForms.add(form);form.requestSubmit(button);}
    catch(error){checkoutError(error.message,form);}finally{checkoutBusy=false;button.disabled=false;}
  },true);
  document.addEventListener('click',async event=>{
    const a=event.target.closest('a[href]');if(!a || !/^\/checkout(?:[/?#]|$)/.test(new URL(a.href,location.origin).pathname))return;
    event.preventDefault();event.stopImmediatePropagation();if(checkoutBusy)return;checkoutBusy=true;
    try{await checkCartPreorders();location.assign(a.href);}catch(error){checkoutError(error.message,a);}finally{checkoutBusy=false;}
  },true);
  window.GeightPurchasing={addItems,validateForm,variantData,validate,integer,json};
})();
